import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  trigger,
  transition,
  style,
  query,
  group,
  animate,
} from '@angular/animations';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { ScrollService } from './scroll.service';
import { PreloaderService } from './components/preloader.service';
import { ThemeService } from './components/theme-switch/theme.service';
import { ToastPageComponent } from './components/toast-page/toast-page.component';

import { environment } from '../environments/environment';

import { clearLazyChunkReloadSessionFlag } from './sentry/sentry-event-filter';
import { AnalyticsService } from './analytics.service';
import { catchError, filter, of, Subject, take, takeUntil, timeout } from 'rxjs';
import { AcceptCookieComponent } from './components/accept-cookie/accept-cookie.component';
import { Meta, Title } from '@angular/platform-browser';
import { UtmService } from './utm.service';
import { GtmInitializerService } from './gtm-initializer.service';
import { TiktokPixelInitializerService } from './tiktok-pixel-initializer.service';
import { FirebaseMessagingService } from './services/firebase-messaging.service';
import { SwUpdateService } from './services/sw-update.service';
import { ChatAccessService } from './services/chat-access.service';
import { ChatbotIaComponent } from './chatbot-ia/chatbot-ia.component';
import { isRestrictedInAppBrowser } from './in-app-browser';

type RdStationLeadTracking = {
  init: (options: { token: string }) => void;
};

type RdStationTrafficSourceCookie = {
  init: (...args: unknown[]) => void;
};

type RdStationFormsIntegration = {
  Integration: {
    integrateAll: (...args: unknown[]) => void;
  };
};

type RdStationPopup = {
  init: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    LeadTracking?: RdStationLeadTracking;
    TrafficSourceCookie?: RdStationTrafficSourceCookie;
    RdstationFormsIntegration?: RdStationFormsIntegration;
    RdstationPopup?: RdStationPopup;
  }
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    LoadingBarModule,
    LoadingBarRouterModule,
    CommonModule,
    ToastPageComponent,
    AcceptCookieComponent,
    ChatbotIaComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [
    trigger('routeAnimations', [
      transition('* <=> *', [
        query(
          ':enter, :leave',
          [
            style({
              position: 'absolute',
              width: '100%',
              opacity: 0,
            }),
          ],
          { optional: true }
        ),
        group([
          query(
            ':leave',
            [animate('700ms ease-in-out', style({ opacity: 0 }))],
            { optional: true }
          ),
          query(
            ':enter',
            [animate('700ms ease-in-out', style({ opacity: 1 }))],
            { optional: true }
          ),
        ]),
      ]),
    ]),
    trigger('fadeInApp', [
      transition('show => hide', [
        animate('1s ease-in-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
  private readonly rdStationScriptUrl =
    'https://d335luupugsy2.cloudfront.net/js/loader-scripts/03524fd5-b016-45a1-a88d-87442d01f64d-loader.js';

  private router = inject(Router);
  private analytics = inject(AnalyticsService);
  private platformId = inject(PLATFORM_ID);
  isDarkMode: boolean = false;
  installPromptEvent: any;
  showInstallButton = false;
  assetBaseUrl: string = '';

  private renderer = inject(Renderer2);
  private chatAccess = inject(ChatAccessService);
  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private scrollService: ScrollService,
    private preloaderService: PreloaderService,
    private themeService: ThemeService,
    private gtmInit: GtmInitializerService,
    private tiktokPixelInit: TiktokPixelInitializerService,
    private meta: Meta,
    private title: Title,
    private utmService: UtmService,
    private firebaseMessagingService: FirebaseMessagingService,
    private swUpdateService: SwUpdateService
  ) {}

  ngOnInit(): void {
    console.log(`


                       ↑↑↑↑↑↑↑↑↑↑
                  ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑
               ↑↑↑↑↑↑↑↑↑↑↑↑  ↑↑↑↑↑↑↑↑↑↑↑↑
             ↑↑↑↑↑↑↑                ↑↑↑↑↑↑↑
           ↑↑↑↑↑↑                      ↑↑↑↑↑↑
          ↑↑↑↑↑
         ↑↑↑↑↑↑↑↑↑↑↑       ↑↑       ↑↑↑↑↑↑↑↑↑↑↑
        ↑↑↑↑↑↑↑↑↑↑↑↑↑↑    ↑↑↑↑    ↑↑↑↑↑↑↑↑↑↑↑↑↑↑
         ↑↑↑↑↑↑↑↑↑↑↑↑↑↑  ↑↑↑↑↑↑  ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑
                  ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑     ↑↑↑↑↑
        ↑↑↑↑↑      ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑      ↑↑↑↑↑
         ↑↑↑↑↑      ↑↑↑↑↑↑↑  ↑↑↑↑↑↑↑      ↑↑↑↑↑
          ↑↑↑↑↑      ↑↑↑↑↑   ↑↑↑↑↑↑      ↑↑↑↑↑
           ↑↑↑↑↑↑     ↑↑↑      ↑↑↑     ↑↑↑↑↑↑
             ↑↑↑↑↑↑↑                ↑↑↑↑↑↑↑
               ↑↑↑↑↑↑↑↑↑↑↑    ↑↑↑↑↑↑↑↑↑↑↑
                  ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑
                       ↑↑↑↑↑↑↑↑↑↑


                WOSI SEGUROS SUSTENTÁVEIS

    `);
    this.assetBaseUrl = environment.assetBaseUrl;

    if (isPlatformBrowser(this.platformId)) {
      const restrictedInAppBrowser = isRestrictedInAppBrowser();

      if (!restrictedInAppBrowser) {
        this.loadRdStationScriptSafely();
      }
      this.utmService.captureUtms();

      this.themeService.listenToSystemThemeChanges();
      this.chatAccess
        .whenReady()
        .pipe(
          take(1),
          timeout(8000),
          catchError(() => of(null)),
          takeUntil(this.destroy$)
        )
        .subscribe(() => this.revealAfterFirstPaint());
      window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        this.installPromptEvent = event;
        this.showInstallButton = true;
      });

      // Evita integrações instáveis em webviews de redes sociais (Android),
      // onde chamadas internas de postMessage podem disparar "Java object is gone".
      if (!restrictedInAppBrowser) {
        this.firebaseMessagingService.setupForegroundMessageHandler();
      }

      // Conversões do Meta Ads dependem do container GTM também no webview
      // do Instagram/Facebook. O TikTok do container é barrado no initGtm.
      this.gtmInit.initGtm();
      if (!restrictedInAppBrowser) {
        this.tiktokPixelInit.initTikTokPixel();
      }
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        clearLazyChunkReloadSessionFlag();
        setTimeout(() => {
          this.analytics.sendPageView(event.urlAfterRedirects);
          if (!isRestrictedInAppBrowser()) {
            this.tiktokPixelInit.trackPageView();
          }
        }, 150);
      });

    // Meta tags adicionais
    this.meta.updateTag({ name: 'theme-color', content: '#0a3829' });

    if (environment.environment !== 'production') {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation'] || 'default';
  }

  instalarApp() {
    if (this.installPromptEvent) {
      this.installPromptEvent.prompt();
      this.installPromptEvent.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('Usuário aceitou instalar o app');
        } else {
          console.log('Usuário recusou instalar o app');
        }
        this.installPromptEvent = null;
        this.showInstallButton = false;
      });
    }
  }

  private hasLocalStorageAccess(): boolean {
    try {
      const testKey = '__wosi_storage_probe__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private revealAfterFirstPaint(): void {
    const hide = () => this.preloaderService.hidePreloader();
    const hideWhenSettled = () => {
      const animating = () => document.querySelector('.ng-animating');
      if (!animating()) {
        hide();
        return;
      }
      const observer = new MutationObserver(() => {
        if (animating()) return;
        observer.disconnect();
        hide();
      });
      observer.observe(document.documentElement, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    };
    const afterPaint = () => hideWhenSettled();
    const whenDocumentReady = () => {
      if (document.readyState === 'complete') {
        afterPaint();
        return;
      }
      window.addEventListener('load', afterPaint, { once: true });
    };

    const fontsReady = document.fonts?.ready;
    if (fontsReady) {
      void fontsReady.then(whenDocumentReady).catch(whenDocumentReady);
      return;
    }
    whenDocumentReady();
  }

  private loadRdStationScriptSafely(): void {
    if (!this.hasLocalStorageAccess()) {
      return;
    }

    this.ensureRdStationGlobalsFallback();

    const hasScriptAlreadyLoaded = !!document.querySelector(
      `script[src="${this.rdStationScriptUrl}"]`
    );

    if (hasScriptAlreadyLoaded) {
      return;
    }

    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = this.rdStationScriptUrl;
    this.renderer.appendChild(document.body, script);
  }

  private ensureRdStationGlobalsFallback(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.LeadTracking) {
      const leadTrackingFallback: RdStationLeadTracking = {
        init: ({ token }) => {
          const currentLeadTracking = window.LeadTracking;
          if (
            currentLeadTracking &&
            currentLeadTracking !== leadTrackingFallback
          ) {
            currentLeadTracking.init({ token });
          }
        },
      };

      // Evita ReferenceError quando o loader da RD Station assume um global
      // que pode não estar pronto ou pode ser bloqueado por extensões/políticas.
      window.LeadTracking = leadTrackingFallback;
    }

    if (!window.TrafficSourceCookie) {
      window.TrafficSourceCookie = {
        init: () => undefined,
      };
    }

    // Popups: o loader chama RdstationPopup.init no `load` antes do script principal existir.
    if (!window.RdstationPopup) {
      let initRetried = false;
      const popupFallback: RdStationPopup = {
        init: (...args: unknown[]) => {
          const delegate = (): boolean => {
            const current = window.RdstationPopup;
            if (
              current &&
              current !== popupFallback &&
              typeof current.init === 'function'
            ) {
              current.init(...args);
              return true;
            }
            return false;
          };

          if (!delegate() && !initRetried) {
            initRetried = true;
            setTimeout(() => {
              delegate();
            }, 0);
          }
        },
      };

      window.RdstationPopup = popupFallback;
    }

    if (!window.RdstationFormsIntegration) {
      let integrateRetried = false;
      const formsFallback: RdStationFormsIntegration = {
        Integration: {
          integrateAll: (...args: unknown[]) => {
            const delegate = (): boolean => {
              const current = window.RdstationFormsIntegration;
              if (
                current &&
                current !== formsFallback &&
                typeof current.Integration?.integrateAll === 'function'
              ) {
                current.Integration.integrateAll(...args);
                return true;
              }
              return false;
            };

            if (!delegate() && !integrateRetried) {
              integrateRetried = true;
              setTimeout(() => {
                delegate();
              }, 0);
            }
          },
        },
      };

      window.RdstationFormsIntegration = formsFallback;
    }
  }
}
